import type { Api } from "../../../data";

/**
 * Rechnung der Agentur speichern und auf Wunsch festschreiben (RechnungErstellen). Reihenfolge wie bisher: Entwurf mit Kopf und
 * Positionen in einer Transaktion (invoice_save_draft bzw. invoice_save_collective), Bestätigung des Steuerfalls, Festschreiben,
 * Beleg ablegen. Neu (Gesamtprüfung 07.10.2026): die Kennung des Entwurfs geht sofort nach dem Speichern an `onSaved`, noch vor
 * dem Festschreiben. Scheiterte das Festschreiben (Steuer-Wächter, fehlende Angaben, Netz), legte der nächste Klick bisher einen
 * zweiten Entwurf an, weil die Seite die Kennung nicht kannte.
 */

export interface DraftSave {
  /** schon gespeicherter Entwurf (aus der Adresse oder aus einem früheren Versuch auf dieser Seite) */
  draftId: string | null;
  header: Record<string, unknown>;
  lines: Record<string, unknown>[];
  approve: boolean;
  /** Steuerfall ausdrücklich bestätigt (0181) */
  confirmTax: boolean;
  /** Sammelrechnung (invoice_save_collective) */
  collective?: boolean;
}

export interface DraftSaveHooks {
  /** sofort nach dem Speichern des Entwurfs: Kennung merken und in die Adresse übernehmen */
  onSaved: (id: string) => void;
  /** Fehler beim Festschreiben, für den die Rechnung mit Begründung festgeschrieben werden kann (Probelauf H3) */
  isCreatorDataGap?: (e: unknown) => boolean;
  /** K-277: die Datenbank verlangt eine Bestätigung des Steuerfalls, die die Fachlogik nicht vorhergesehen hat */
  isTaxConfirmRequest?: (e: unknown) => boolean;
  /** Steuerprüfung L6: unmittelbar vor dem Festschreiben (USt-IdNr. bei VIES prüfen); Fehler hier verhindern das Festschreiben nicht */
  beforeApprove?: (id: string) => Promise<unknown>;
  /** Beleg einmal ablegen (GoBD); Fehler hier verhindern das Ergebnis nicht */
  archive?: (id: string) => Promise<unknown>;
}

export async function saveAgencyInvoice(api: Pick<Api, "rpc" | "update">, x: DraftSave, hooks: DraftSaveHooks): Promise<{ id: string; begruendung: boolean; steuer?: boolean }> {
  const id = await api.rpc<string>(x.collective ? "invoice_save_collective" : "invoice_save_draft", { p_invoice: x.draftId || null, p_header: x.header, p_lines: x.lines });
  if (!x.draftId) hooks.onSaved(id);
  if (!x.approve) return { id, begruendung: false };
  const now = new Date().toISOString();
  await hooks.beforeApprove?.(id).catch(() => null);
  if (x.collective) {
    try {
      await api.update("invoices", [["id", "eq", id]], x.confirmTax ? { status: "approved", tax_treatment_confirmed_at: now } : { status: "approved" });
    } catch (e) {
      if (!x.confirmTax && hooks.isTaxConfirmRequest?.(e)) return { id, begruendung: false, steuer: true };
      throw e;
    }
  } else {
    // Bestätigung im Entwurf ablegen (eigener Schritt: bleibt erhalten, falls das Festschreiben eine Begründung verlangt)
    if (x.confirmTax) await api.update("invoices", [["id", "eq", id]], { tax_treatment_confirmed_at: now });
    try {
      await api.update("invoices", [["id", "eq", id]], { status: "approved" });
    } catch (e) {
      if (!x.confirmTax && hooks.isTaxConfirmRequest?.(e)) return { id, begruendung: false, steuer: true };
      // Rechnung im Namen des Creators ohne Steuernummer bzw. Bankverbindung: der Entwurf ist gespeichert, die Rechnung fragt nach der Begründung
      if (!hooks.isCreatorDataGap?.(e)) throw e;
      return { id, begruendung: true };
    }
  }
  await hooks.archive?.(id).catch(() => null);
  return { id, begruendung: false };
}

/** Adresse mit der Kennung des Entwurfs (?entwurf=…), übrige Angaben bleiben */
export function withDraftParam(params: URLSearchParams, id: string): URLSearchParams {
  const next = new URLSearchParams(params);
  next.set("entwurf", id);
  return next;
}
